import { useEffect, useState } from "react";
import { api, stageLabel, inr, LOST_REASONS } from "../lib/api";
import { Card } from "../components/ui/card";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";

const brand = "#0757C6";
const brandYellow = "#FFD819";
const grid = "#1e2a44";
const muted = "#7d90b6";
const tooltipStyle = { background: "#0f1a30", border: "1px solid #263659", borderRadius: 6, color: "#e5eefb", fontSize: 12 };

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const byStage = Object.entries(data.projects_by_stage || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: stageLabel(k), value: v }));
  const overdueByFn = Object.entries(data.overdue_by_function || {}).map(([k, v]) => ({ name: k, value: v }));
  const ownerWorkload = Object.entries(data.owner_workload || {}).map(([k, v]) => ({ name: k, tasks: v }));
  const pipeline = Object.entries(data.revenue_pipeline || {}).map(([k, v]) => ({ name: k, value: v }));
  const ticketStatus = Object.entries(data.ticket_status || {}).map(([k, v]) => ({ name: k, value: v }));
  const procStatus = Object.entries(data.procurement_status || {}).map(([k, v]) => ({ name: k, value: v }));

  const reasonLabel = (k) => LOST_REASONS.find((r) => r.key === k)?.label || k;
  const deliveredMonths = Object.entries(data.delivered_by_month || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => ({ name: k, count: v, value: (data.delivered_value_by_month || {})[k] || 0 }));
  const lostByReason = Object.entries(data.lost_by_reason || {})
    .map(([k, v]) => ({ name: reasonLabel(k), key: k, value: v, amount: (data.lost_value_by_reason || {})[k] || 0 }));
  const lostProjects = data.lost_projects || [];
  const wonCount = data.won_count || 0;
  const lostCount = data.lost_count || 0;
  const convRate = data.conversion_rate || 0;

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Analytics</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Reports</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="conversion-strip">
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Delivered</div>
          <div className="font-mono text-2xl mt-0.5 text-emerald-400">{wonCount}</div>
          <div className="text-[11px] text-muted-foreground">Completed / AMC</div>
        </Card>
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lost</div>
          <div className="font-mono text-2xl mt-0.5 text-red-400">{lostCount}</div>
          <div className="text-[11px] text-muted-foreground">Dropped inquiries</div>
        </Card>
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Conversion</div>
          <div className="font-mono text-2xl mt-0.5" style={{ color: brandYellow }}>{convRate}%</div>
          <div className="text-[11px] text-muted-foreground">Won / (Won + Lost)</div>
        </Card>
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Value delivered</div>
          <div className="font-mono text-2xl mt-0.5 text-primary">
            {inr(Object.values(data.delivered_value_by_month || {}).reduce((a, b) => a + b, 0))}
          </div>
          <div className="text-[11px] text-muted-foreground">All-time handover value</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4 bg-card border-border">
          <h3 className="font-display font-semibold mb-1">Projects across the pipeline</h3>
          <p className="text-xs text-muted-foreground mb-3">Count of active projects at each stage</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={byStage} margin={{ left: -6, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="pipelineFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={brand} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={brand} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis dataKey="name" stroke={muted} tick={{ fontSize: 10 }} angle={-20} height={50} textAnchor="end" />
              <YAxis stroke={muted} tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="value" stroke={brand} strokeWidth={2} fill="url(#pipelineFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 bg-card border-border">
          <h3 className="font-display font-semibold mb-1">Revenue pipeline value</h3>
          <p className="text-xs text-muted-foreground mb-3">Total ₹ per bucket (early → completed)</p>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={pipeline} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis dataKey="name" stroke={muted} tick={{ fontSize: 11 }} />
              <YAxis stroke={muted} tick={{ fontSize: 11 }} tickFormatter={(v) => (v / 100000).toFixed(0) + "L"} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => inr(v)} />
              <Line type="monotone" dataKey="value" stroke={brandYellow} strokeWidth={2.5} dot={{ r: 4, fill: brandYellow }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 bg-card border-border">
          <h3 className="font-display font-semibold mb-1">Overdue tasks by function</h3>
          <p className="text-xs text-muted-foreground mb-3">Where the delays are piling up</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={overdueByFn} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis dataKey="name" stroke={muted} tick={{ fontSize: 11 }} />
              <YAxis stroke={muted} tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: "#ef4444" }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 bg-card border-border">
          <h3 className="font-display font-semibold mb-1">Owner workload</h3>
          <p className="text-xs text-muted-foreground mb-3">Open tasks per team member</p>
          <div className="space-y-2 pt-1">
            {ownerWorkload.length === 0 && <div className="text-sm text-muted-foreground">No workload data.</div>}
            {ownerWorkload.map((w) => {
              const max = Math.max(...ownerWorkload.map((x) => x.tasks), 1);
              const pct = (w.tasks / max) * 100;
              return (
                <div key={w.name}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{w.name}</span>
                    <span className="font-mono">{w.tasks}</span>
                  </div>
                  <div className="h-2 rounded bg-secondary/40 overflow-hidden">
                    <div className="h-full bg-[hsl(var(--brand-blue))] transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-4 bg-card border-border">
          <h3 className="font-display font-semibold mb-1">Service tickets</h3>
          <p className="text-xs text-muted-foreground mb-3">Open vs resolved</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={ticketStatus} dataKey="value" nameKey="name" outerRadius={80} innerRadius={55} paddingAngle={2} stroke="none">
                {ticketStatus.map((e, i) => <Cell key={e.name} fill={[brand, brandYellow, "#10b981", "#ef4444", muted][i % 5]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: muted }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4 bg-card border-border">
          <h3 className="font-display font-semibold mb-1">Procurement in flight</h3>
          <p className="text-xs text-muted-foreground mb-3">Where items are in the supply chain</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={procStatus} dataKey="value" nameKey="name" outerRadius={80} innerRadius={55} paddingAngle={2} stroke="none">
                {procStatus.map((e, i) => <Cell key={e.name} fill={[muted, brand, brandYellow, "#10b981", "#ef4444"][i % 5]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: muted }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4 bg-card border-border" data-testid="delivered-by-month-card">
          <h3 className="font-display font-semibold mb-1">Delivered projects by month</h3>
          <p className="text-xs text-muted-foreground mb-3">Handovers over time — value + count</p>
          {deliveredMonths.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No handovers yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={deliveredMonths} margin={{ left: 0, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                <XAxis dataKey="name" stroke={muted} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" stroke={muted} tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" stroke={muted} tick={{ fontSize: 11 }} tickFormatter={(v) => (v / 100000).toFixed(0) + "L"} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, key) => key === "value" ? inr(v) : v}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: muted }} />
                <Bar yAxisId="left" dataKey="count" name="Projects" fill={brand} radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="value" name="Value (₹)" fill={brandYellow} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-4 bg-card border-border" data-testid="lost-by-reason-card">
          <h3 className="font-display font-semibold mb-1">Lost inquiries by reason</h3>
          <p className="text-xs text-muted-foreground mb-3">Why deals slipped away</p>
          {lostByReason.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No lost inquiries recorded.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={lostByReason} dataKey="value" nameKey="name" outerRadius={85} innerRadius={55} paddingAngle={2} stroke="none">
                  {lostByReason.map((e, i) => (
                    <Cell key={e.key} fill={["#ef4444", "#f97316", "#eab308", "#a3a3a3", "#6366f1", "#8b5cf6", "#64748b"][i % 7]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(v, _n, item) => [`${v} deal(s) · ${inr(item?.payload?.amount)}`, item?.payload?.name]} />
                <Legend wrapperStyle={{ fontSize: 11, color: muted }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {lostProjects.length > 0 && (
        <Card className="p-4 bg-card border-border" data-testid="lost-projects-list">
          <h3 className="font-display font-semibold mb-3">Lost projects — details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {lostProjects.map((p) => (
              <div key={p.id} className="p-2 rounded border border-border bg-secondary/20">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.client_name}</div>
                    <div className="text-xs text-red-400">{reasonLabel(p.lost_reason)}</div>
                    {p.lost_notes && <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{p.lost_notes}</div>}
                  </div>
                  <div className="font-mono text-primary text-sm whitespace-nowrap">{inr(p.project_value)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4 bg-card border-border">
        <h3 className="font-display font-semibold mb-3">Payment-pending projects</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(data.payment_pending_list || []).map((p) => (
            <div key={`${p.client_name}-${p.stage}`} className="flex justify-between p-2 rounded border border-border bg-secondary/20">
              <div>
                <div className="text-sm font-medium">{p.client_name}</div>
                <div className="text-xs text-muted-foreground">{stageLabel(p.stage)}</div>
              </div>
              <div className="font-mono text-primary">{inr(p.amount)}</div>
            </div>
          ))}
          {(data.payment_pending_list || []).length === 0 && <div className="text-sm text-muted-foreground">No pending payments.</div>}
        </div>
      </Card>
    </div>
  );
}
